import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import {
  LayoutDashboard,
  CheckSquare,
  FolderKanban,
  Calendar,
  Users,
  ClipboardCheck,
  Settings,
  Bell,
  ChevronLeft,
  ChevronRight,
  Menu as MenuIcon,
  Timer,
  LogOut,
  User,
  Send,
  Building2,
} from 'lucide-react';

import { useAppSelector, useAppDispatch } from '@/store';
import { selectCurrentUser, logout } from '@/store/slices/authSlice';
import { logout as apiLogout } from '@/services/api/authService';
import { selectActiveTimers, pauseAllTimersAsync } from '@/store/slices/timerSlice';
import { selectSidebarView, toggleSidebar } from '@/store/slices/uiSlice';
import { UserRole } from '@/types';
import { normalizeRole, type EffectiveRole } from '@/shared/utils/roles';
import ActiveTimerBar from './ActiveTimerBar';
import GlobalModals from './GlobalModals';
import MobileBottomNav from './MobileBottomNav';
import MobileMoreMenu from './MobileMoreMenu';
import MobileHeader from './MobileHeader';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

// Navigation items with role-based visibility
const getNavigationItems = (role: EffectiveRole) => {
  const baseItems = [
    {
      path: '/',
      label: 'Dashboard',
      icon: LayoutDashboard,
      roles: 'all',
    },
    {
      path: '/tasks',
      label: 'My Tasks',
      icon: CheckSquare,
      roles: 'all',
    },
    {
      path: '/delegated',
      label: 'Delegated Tasks',
      icon: Send,
      roles: 'all',
    },
    {
      path: '/projects',
      label: 'Projects',
      icon: FolderKanban,
      roles: 'all',
    },
    {
      path: '/calendar',
      label: 'Calendar',
      icon: Calendar,
      roles: 'all',
    },
  ];

  const managerItems = [
    {
      path: '/team',
      label: 'Team',
      icon: Users,
      roles: ['admin', 'manager'],
    },
    {
      path: '/departments',
      label: 'Departments',
      icon: Building2,
      roles: ['admin', 'manager'],
    },
    {
      path: '/reviews',
      label: 'Reviews & Approval',
      icon: ClipboardCheck,
      roles: ['admin', 'manager'],
    },
  ];

  const adminItems = [
    {
      path: '/users',
      label: 'Users',
      icon: User,
      roles: ['admin'],
    },
  ];

  const settingsItem = {
    path: '/settings',
    label: 'Settings',
    icon: Settings,
    roles: ['admin', 'manager'],
  };

  const allItems = [...baseItems, ...managerItems, ...adminItems, settingsItem];

  return allItems.filter((item) => {
    if (item.roles === 'all') return true;
    return (item.roles as EffectiveRole[]).includes(role);
  });
};

/**
 * Layout Component
 * Main application layout with sidebar navigation and header
 * Implements role-based navigation visibility
 * Mobile-first responsive design with bottom navigation
 */
const Layout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const user = useAppSelector(selectCurrentUser);
  const activeTimers = useAppSelector(selectActiveTimers);
  const sidebarView = useAppSelector(selectSidebarView);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // State for logout confirmation dialog when active timers are running
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  // State to prevent multiple logout attempts during timer pause operation
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isCollapsed = sidebarView === 'collapsed';
  const drawerWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const effectiveRole = normalizeRole(user?.role || UserRole.INDIVIDUAL_CONTRIBUTOR);
  const navigationItems = getNavigationItems(effectiveRole);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      dispatch(toggleSidebar());
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handles user logout action with active timer detection
   * 
   * Flow:
   * 1. Close profile menu
   * 2. Check if any timers are currently running (status === 'running')
   * 3. If running timers exist: Show confirmation dialog
   * 4. If no running timers: Proceed directly with logout
   * 
   * This prevents users from accidentally losing timer state on logout.
   * Paused timers don't trigger dialog since they're already persisted.
   */
  const handleLogout = async () => {
    handleProfileMenuClose();
    
    // Check if there are active running timers (paused timers are already saved)
    const runningTimers = activeTimers.filter(t => t.status === 'running');
    if (runningTimers.length > 0) {
      // Show confirmation dialog to user
      setShowLogoutDialog(true);
      return;
    }
    
    // No active running timers, proceed with standard logout
    await apiLogout();
    dispatch(logout());
    navigate('/login');
  };

  /**
   * Confirms logout after user acknowledges active timers
   * 
   * Flow:
   * 1. Set loading state to prevent duplicate logout attempts
   * 2. Pause all running timers via backend API (preserves timer state in DB)
   * 3. Call backend logout endpoint (invalidates JWT)
   * 4. Clear Redux auth state
   * 5. Navigate to login page
   * 
   * Error handling: If timer pause fails, still proceeds with logout
   * to prevent users from being stuck. Timer state may be lost in this case.
   */
  const confirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Pause all running timers before logout (saves to database)
      await dispatch(pauseAllTimersAsync()).unwrap();
      // Call backend logout endpoint
      await apiLogout();
      // Clear Redux authentication state
      dispatch(logout());
      // Redirect to login page
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still proceed with logout even if timer pause fails
      // This prevents users from being unable to logout
      await apiLogout();
      dispatch(logout());
      navigate('/login');
    } finally {
      // Reset loading state and close dialog
      setIsLoggingOut(false);
      setShowLogoutDialog(false);
    }
  };

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo/Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          px: 2,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {!isCollapsed && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                letterSpacing: '-0.5px',
              }}
            >
              Task Management System
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              Nestbotics
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton
            size="small"
            onClick={handleDrawerToggle}
            sx={{ ml: isCollapsed ? 0 : 'auto' }}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, px: 1, py: 2 }}>
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = isActivePath(item.path);

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={isCollapsed ? item.label : ''} placement="right">
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: '8px',
                    minHeight: 44,
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    px: isCollapsed ? 2 : 2,
                    bgcolor: isActive ? 'primary.50' : 'transparent',
                    color: isActive ? 'primary.main' : 'text.secondary',
                    '&:hover': {
                      bgcolor: isActive ? 'primary.100' : 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isCollapsed ? 0 : 40,
                      color: 'inherit',
                    }}
                  >
                    <Icon size={20} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 600 : 500,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* Settings at bottom */}
      <Box sx={{ px: 1, pb: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <ListItem disablePadding>
          <Tooltip title={isCollapsed ? 'Settings' : ''} placement="right">
            <ListItemButton
              onClick={() => handleNavigation('/settings')}
              sx={{
                borderRadius: '8px',
                minHeight: 44,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 2 : 2,
                color: isActivePath('/settings') ? 'primary.main' : 'text.secondary',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isCollapsed ? 0 : 40,
                  color: 'inherit',
                }}
              >
                <Settings size={20} />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile Header - Only shown on mobile */}
      {isMobile && <MobileHeader />}

      {/* Desktop App Bar - Hidden on mobile */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon size={24} />
          </IconButton>

          {/* Page title - could be dynamic */}
          <Typography
            variant="h6"
            sx={{ color: 'text.primary', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}
          >
            {/* Dynamic based on route */}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {/* Active Timer Indicator */}
          {activeTimers.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mr: 2,
                px: 2,
                py: 0.5,
                bgcolor: 'success.50',
                borderRadius: '20px',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/tasks')}
            >
              <Timer size={16} className="timer-running" style={{ color: '#16a34a' }} />
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                {activeTimers.length} Timer{activeTimers.length > 1 ? 's' : ''} Active
              </Typography>
            </Box>
          )}

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton sx={{ color: 'text.secondary' }}>
              <Badge badgeContent={3} color="error" variant="dot">
                <Bell size={20} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User menu */}
          <Box sx={{ ml: 2 }}>
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0.5 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {user?.firstName?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: { minWidth: 200, mt: 1 },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user?.displayName || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { handleNavigation('/settings'); handleProfileMenuClose(); }}>
                <User size={16} style={{ marginRight: 12 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <LogOut size={16} style={{ marginRight: 12 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          // Add bottom padding for mobile nav
          pb: { xs: '80px', md: 0 },
        }}
      >
        {/* Spacer for fixed header */}
        <Box sx={{ height: { xs: 56, md: 64 } }} />
        
        {/* Active Timer Bar (shown when timer(s) are running) - desktop only */}
        {activeTimers.length > 0 && !isMobile && <ActiveTimerBar />}

        {/* Page Content - tighter padding on mobile */}
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav onMoreClick={() => setMobileMoreOpen(true)} />}

      {/* Mobile More Menu */}
      <MobileMoreMenu
        open={mobileMoreOpen}
        onClose={() => setMobileMoreOpen(false)}
      />

      {/* Global Modals */}
      <GlobalModals />

      {/* 
        Logout Confirmation Dialog - Shows when user attempts logout with running timers
        
        Purpose: Inform user that their timers will be paused and allow them to cancel
        
        Features:
        - Shows count of running timers
        - Explains timers will be auto-paused and can be resumed later
        - Disables close/cancel during pause operation (prevents race conditions)
        - Shows loading spinner during pause/logout operation
        - Warning color (yellow) to indicate important action
      */}
      <Dialog
        open={showLogoutDialog}
        onClose={() => !isLoggingOut && setShowLogoutDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Active Timers Running</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have {activeTimers.filter(t => t.status === 'running').length} active timer(s) running. 
            Your timers will be paused automatically when you logout. 
            You can resume them when you login again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* Cancel button - disabled during logout to prevent race conditions */}
          <Button 
            onClick={() => setShowLogoutDialog(false)} 
            disabled={isLoggingOut}
          >
            Cancel
          </Button>
          {/* Confirm button - triggers timer pause then logout */}
          <Button
            variant="contained"
            color="warning"
            onClick={confirmLogout}
            disabled={isLoggingOut}
            startIcon={isLoggingOut ? <CircularProgress size={16} /> : <LogOut size={16} />}
          >
            {isLoggingOut ? 'Pausing & Logging Out...' : 'Pause & Logout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
